// Settings card "صيانة النظام" (original/src/App.tsx:16766-17240, `case "maintenance"`), ADR-08 §2.1 "copy, then cut
// logic". [C38] B6 part 2. Kept from the old card: the section labels (small grey caps with an icon, a top border),
// the rose gradient box of the Super Manager's backup block (:16902-16911), the emerald "تصدير البيانات" tile of the
// data-backup grid (:17297-17311), the rose factory-reset block with its warning line and red full-width button
// (:17193-17229). Cut (decisions): the folder picker and silent shift backups (the desktop helper does that, D-152,
// D-205), Telegram / Gmail / Google secret fields (D-153), "import data" (D-154: old files only feed the importer),
// the document-counter reset (D-128), the support / IP block and the language switch (they live in their own cards).
// Added (new elements, owner sign-off pending, D-360): the backup key status with "create / replace / test the sheet",
// the optional platform share (D-208), the list of kept copies, and the dataset versions (D-062). Every figure and
// date comes from the container; strings only through i18n.
import { Archive, Database, Download, Eye, EyeOff, KeyRound, RefreshCw, RotateCcw, Shield, ShieldCheck, Trash2, X } from 'lucide-react';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { useT } from '../i18n-context.js';

export interface MaintenanceCopyRow {
  readonly id: string;
  readonly label: string; // e.g. "نسخة كاملة — ليلية"
  readonly createdAt: string; // formatted
  readonly size: string; // formatted
  readonly available: boolean;
  readonly highlight: boolean; // the copy made before a factory reset
}

export interface MaintenanceBackupView {
  readonly configured: boolean;
  readonly key: { readonly keyId: string; readonly since: string; readonly verifiedAt: string | null } | null;
  readonly platformShare: { readonly since: string } | null;
  readonly copies: readonly MaintenanceCopyRow[];
}

export interface MaintenanceDatasetView {
  readonly currentVersion: number;
  readonly versions: ReadonlyArray<{ readonly version: number; readonly current: boolean; readonly status: string; readonly detail: string; readonly viewable: boolean }>;
  readonly viewing: number | null;
  readonly rollback: { readonly version: number; readonly allowed: boolean; readonly operations: number } | null;
  readonly request: { readonly text: string; readonly tone: 'busy' | 'ok' | 'error' } | null;
  /** Why a reset cannot start now (no key, no export volume, open envelopes, a request running), or null. */
  readonly resetBlocked: string | null;
}

export interface MaintenanceCardBodyProps {
  readonly isSuperManager: boolean;
  readonly canDownload: boolean;
  readonly backup: MaintenanceBackupView | null;
  readonly datasets: MaintenanceDatasetView | null;
  readonly busy?: boolean;
  readonly onCreateKey: () => void;
  readonly onReplaceKey: () => void;
  readonly onVerifyKey: () => void;
  readonly onShare: () => void;
  readonly onDownloadNow: () => void;
  readonly onDownloadCopy: (id: string) => void;
  readonly onView: (version: number) => void;
  readonly onCloseView: () => void;
  readonly onRollback: (version: number) => void;
  readonly onReset: () => void;
}

const SECTION = 'pt-6 border-t border-gray-100 dark:border-[#30363d] space-y-4';
const LABEL = 'block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-1 uppercase tracking-widest flex items-center gap-2';
const SMALL_BTN = 'py-2.5 px-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center justify-center gap-2';

export function MaintenanceCardBody(p: MaintenanceCardBodyProps) {
  const t = useT();
  const b = p.backup;
  const d = p.datasets;
  return (
    <div className="space-y-6 text-right" data-testid="maintenance-card">
      {p.isSuperManager && b && (
        <div className="space-y-4">
          <label className={LABEL}>
            <Shield size={14} className="text-rose-500" />
            {t('maint.backup_title')}
          </label>
          <div className="p-5 bg-gradient-to-br from-rose-50 to-rose-100/30 dark:from-rose-950/10 dark:to-transparent border border-rose-100 dark:border-rose-900/30 rounded-2xl relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-24 h-24 bg-rose-500/5 rounded-full blur-2xl" />
            <div className="relative z-10 flex flex-col gap-3">
              <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed font-semibold">{t('maint.backup_note')}</p>
              {!b.configured && <p className="text-[11px] font-bold text-amber-700 dark:text-amber-400" data-testid="maint-not-configured">{t('maint.not_configured')}</p>}
              <div className="flex items-center gap-2 text-xs font-black text-gray-800 dark:text-[#f0f6fc]" data-testid="maint-key-state">
                <KeyRound size={16} className={b.key ? (b.key.verifiedAt ? 'text-emerald-600' : 'text-amber-500') : 'text-rose-500'} />
                {b.key ? (b.key.verifiedAt ? t('maint.key_verified', { id: b.key.keyId.slice(0, 8), date: b.key.verifiedAt }) : t('maint.key_unverified', { id: b.key.keyId.slice(0, 8), date: b.key.since })) : t('maint.key_none')}
              </div>
              {!b.key ? (
                <button type="button" onClick={p.onCreateKey} disabled={p.busy} className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-rose-600/20 disabled:opacity-50" data-testid="maint-create-key">
                  <KeyRound size={16} />
                  {t('maint.create_key')}
                </button>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <button type="button" onClick={p.onVerifyKey} disabled={p.busy} className={`${SMALL_BTN} bg-white dark:bg-[#0d1117] border border-rose-100 dark:border-rose-900/30 text-rose-700 dark:text-rose-400 hover:bg-rose-50`} data-testid="maint-verify-key">
                    <ShieldCheck size={14} />
                    {t('maint.verify_key')}
                  </button>
                  <button type="button" onClick={p.onShare} disabled={p.busy} className={`${SMALL_BTN} bg-white dark:bg-[#0d1117] border border-rose-100 dark:border-rose-900/30 text-gray-700 dark:text-[#f0f6fc] hover:bg-rose-50`} data-testid="maint-share">
                    <Archive size={14} />
                    {b.platformShare ? t('maint.share_on') : t('maint.share_off')}
                  </button>
                  <button type="button" onClick={p.onReplaceKey} disabled={p.busy} className={`${SMALL_BTN} bg-white dark:bg-[#0d1117] border border-rose-100 dark:border-rose-900/30 text-gray-700 dark:text-[#f0f6fc] hover:bg-rose-50`} data-testid="maint-replace-key">
                    <RefreshCw size={14} />
                    {t('maint.replace_key')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {p.canDownload && b && (
        <div className={SECTION}>
          <label className={LABEL}>
            <Database size={14} />
            {t('dataBackup')}
          </label>
          <button type="button" onClick={p.onDownloadNow} disabled={p.busy || !b.key || !b.configured} className="w-full p-4 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 rounded-2xl flex flex-col items-center gap-2 hover:bg-emerald-100 transition-all group disabled:opacity-50" data-testid="maint-download">
            <Download className="text-emerald-600 group-hover:scale-110 transition-transform" />
            <span className="text-xs font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">{t('exportData')}</span>
          </button>
          <div className="space-y-1.5" data-testid="maint-copies">
            <p className="text-[10px] font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest">{t('maint.copies')}</p>
            {b.copies.length === 0 && <p className="text-[11px] text-gray-500 dark:text-[#8b949e]">{t('maint.copies_none')}</p>}
            {b.copies.map((c) => (
              <div key={c.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border text-[11px] ${c.highlight ? 'border-rose-200 bg-rose-50/60 dark:border-rose-900/30 dark:bg-rose-950/10' : 'border-gray-100 bg-gray-50 dark:border-[#30363d] dark:bg-[#05070a]'}`}>
                <div className="flex flex-col">
                  <span className="font-black text-gray-800 dark:text-[#f0f6fc]">{c.label}</span>
                  <span className="text-gray-500 dark:text-[#8b949e] font-mono" dir="ltr">
                    {c.createdAt} · {c.size}
                  </span>
                </div>
                {c.available ? (
                  <button type="button" onClick={() => p.onDownloadCopy(c.id)} disabled={p.busy} className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-900/20" title={t('maint.copy_download')} aria-label={t('maint.copy_download')}>
                    <Download size={16} />
                  </button>
                ) : (
                  <span className="text-[10px] text-gray-400">{t('maint.copy_removed')}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {p.isSuperManager && d && (
        <div className={SECTION} data-testid="maint-datasets">
          <label className={LABEL}>
            <Archive size={14} />
            {t('maint.datasets_title')}
          </label>
          {d.request && (
            <div className={`p-3 rounded-xl text-[11px] font-bold border ${d.request.tone === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-950/10 dark:border-rose-900/30 dark:text-rose-400' : d.request.tone === 'busy' ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/20 dark:border-amber-900/30 dark:text-amber-300' : 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-900/10 dark:border-emerald-900/30 dark:text-emerald-400'}`}>
              {d.request.text}
            </div>
          )}
          {d.versions.map((v) => (
            <div key={v.version} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-gray-100 bg-gray-50 dark:border-[#30363d] dark:bg-[#05070a] text-[11px]">
              <div className="flex flex-col">
                <span className="font-black text-gray-800 dark:text-[#f0f6fc]">{t('maint.version', { n: v.version })} — {v.status}</span>
                <span className="text-gray-500 dark:text-[#8b949e]">{v.detail}</span>
              </div>
              {d.viewing === v.version ? (
                <button type="button" onClick={p.onCloseView} disabled={p.busy} className={`${SMALL_BTN} bg-indigo-600 text-white hover:bg-indigo-700`}>
                  <EyeOff size={14} />
                  {t('maint.view_close')}
                </button>
              ) : v.viewable ? (
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => p.onView(v.version)} disabled={p.busy} className={`${SMALL_BTN} bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] text-gray-700 dark:text-[#f0f6fc] hover:bg-gray-100`} data-testid={`maint-view-${v.version}`}>
                    <Eye size={14} />
                    {t('maint.view_open')}
                  </button>
                  {d.rollback?.version === v.version && d.rollback.allowed && (
                    <button type="button" onClick={() => p.onRollback(v.version)} disabled={p.busy} className={`${SMALL_BTN} bg-amber-500 text-white hover:bg-amber-600`} data-testid="maint-rollback">
                      <RotateCcw size={14} />
                      {t('maint.rollback')}
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          ))}
          {d.rollback && !d.rollback.allowed && d.rollback.operations > 0 && <p className="text-[10px] text-gray-500 dark:text-[#8b949e] leading-relaxed">{t('maint.rollback_closed', { n: d.rollback.operations })}</p>}
        </div>
      )}

      {p.isSuperManager && d && (
        <div className="pt-6 border-t border-gray-100 dark:border-[#30363d]">
          <label className="block text-xs font-bold text-rose-500 mb-3 uppercase tracking-widest flex items-center gap-2">
            <Trash2 size={14} />
            {t('maint.reset_title')}
          </label>
          <div className="p-4 bg-rose-50 dark:bg-rose-900/10 rounded-xl border border-rose-100 dark:border-rose-900/30">
            <p className="text-[10px] text-rose-600 dark:text-rose-400 font-bold mb-4 leading-relaxed">{t('maint.reset_warning')}</p>
            {d.resetBlocked && <p className="text-[10px] text-gray-600 dark:text-gray-400 font-bold mb-3 leading-relaxed" data-testid="maint-reset-blocked">{d.resetBlocked}</p>}
            <button type="button" onClick={p.onReset} disabled={p.busy || !!d.resetBlocked} className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-rose-600/20 disabled:opacity-50" data-testid="maint-reset">
              <RefreshCw size={16} />
              {t('maint.reset_button')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Modal shell of the maintenance dialogs (the PinDialog frame: dark header, rounded-3xl body). */
export function MaintenanceDialog({ title, onClose, children, wide, testId }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean; testId?: string }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid={testId ?? 'maint-dialog'}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`bg-white dark:bg-[#0d1117] w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl relative z-10 border border-gray-100 dark:border-[#30363d]`}>
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-gray-900 text-white dark:bg-[#161b22]">
          <h3 className="text-lg font-black flex items-center gap-2 tracking-tight">
            <ShieldCheck className="text-rose-500" />
            {title}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white transition-colors" aria-label="close">
            <X size={22} />
          </button>
        </div>
        <div className="p-6 space-y-4 text-right">{children}</div>
      </motion.div>
    </div>
  );
}
